export default {
  common: {
    appName: 'ElderCare',
    loading: 'Loading...',
    retry: 'Retry',
    goBack: 'Go Home',
    logout: 'Logout',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    search: 'Search',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    systemError: 'A system error occurred. Please refresh and try again',
    weekDays: {
      sun: 'Sun',
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
    },
  },
  api: {
    error: {
      400: 'Bad request',
      401: 'Session expired, please login again',
      403: 'Permission denied',
      404: 'Resource not found',
      409: 'Data conflict, please refresh and retry',
      422: 'Validation failed',
      429: 'Too many requests, please try later',
      500: 'Internal server error',
      502: 'Service temporarily unavailable',
      503: 'Service under maintenance',
      default: 'Request failed ({status})',
      network: 'Network connection failed',
    },
  },
  nav: {
    home: 'Home',
    login: 'Login',
    dashboard: 'Dashboard',
    health: 'Health',
    medication: 'Medication',
    sos: 'Emergency',
    family: 'Family',
    article: 'Articles',
    checkin: 'Check-in',
    settings: 'Settings',
    about: 'About',
  },
  footer: {
    rights: 'All Rights Reserved',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
  },
  index: {
    hero: {
      title: 'ElderCare - Smart Health Guardian for Seniors',
      subtitle: 'A health management platform designed for seniors, keeping families informed and seniors healthy',
      cta: 'Get Started',
      learnMore: 'Learn More',
    },
    features: {
      title: 'Core Features',
      health: { title: 'Health Monitoring', desc: 'Track blood pressure, blood sugar, heart rate and more with smart alerts' },
      medication: { title: 'Medication Management', desc: 'Medication reminders and interaction checks, never miss a dose' },
      sos: { title: 'Emergency Call', desc: 'One-touch SOS with automatic family notification and location sharing' },
      family: { title: 'Family Care', desc: 'Remote monitoring, instant alerts, and data sharing for families' },
      article: { title: 'Health Articles', desc: 'Authoritative health knowledge and personalized wellness guides' },
      checkin: { title: 'Daily Check-in', desc: 'Simple daily tracking to build healthy habits' },
    },
    elderly: {
      title: 'Senior-Friendly Design',
      desc: 'Designed for seniors - easy to use, easy to see, easy to tap',
      font: 'Large Fonts',
      touch: 'Large Touch Targets',
      contrast: 'High Contrast Colors',
    },
  },
  login: {
    title: 'ElderCare',
    loginTab: 'Login',
    registerTab: 'Register',
    name: 'Name',
    email: 'Email',
    password: 'Password',
    role: 'I am',
    elderRole: 'Senior',
    familyRole: 'Family',
    bindCode: 'Binding Code',
    bindCodeHint: 'Enter the binding code from the senior account to link and view health data',
    loginBtn: 'Login',
    registerBtn: 'Register',
    verifyHint: 'A verification email will be sent to your email after registration',
    error: 'Operation failed, please retry',
    nameTooShort: 'Name must be at least 2 characters',
    emailInvalid: 'Please enter a valid email address',
    passwordTooShort: 'Password must be at least 8 characters',
  },
  dashboard: {
    title: 'Health Dashboard',
    metrics: {
      bloodPressure: 'Blood Pressure',
      bloodSugar: 'Blood Sugar',
      heartRate: 'Heart Rate',
      weight: 'Weight',
      sleep: 'Sleep',
      temperature: 'Temperature',
    },
    quickActions: 'Quick Actions',
    recordHealth: 'Record Health',
    takeMedicine: 'Take Medicine',
    sos: 'Emergency Call',
    checkinStatus: 'Today Check-in',
    checkedIn: 'Checked In Today',
    notCheckedIn: 'Not Checked In Today',
    goCheckin: 'Check In',
    medicationReminders: 'Medication Reminders',
    noReminders: 'No medication reminders',
    taken: 'Taken',
  },
  health: {
    title: 'Health Records',
    inputTitle: 'Data Entry',
    systolic: 'Systolic (mmHg)',
    diastolic: 'Diastolic (mmHg)',
    bloodSugar: 'Blood Sugar (mmol/L)',
    heartRate: 'Heart Rate (bpm)',
    weight: 'Weight (kg)',
    sleepHours: 'Sleep (hours)',
    temperature: 'Temperature (°C)',
    submit: 'Submit Record',
    trend: 'Trends',
    days: 'days',
    aiAnalysis: 'AI Health Analysis',
    analyze: 'Analyze',
    noAnalysis: 'Click the button above to get an AI health analysis report',
    history: 'History',
    noHistory: 'No history records',
    chartLabelSystolic: 'Systolic Pressure',
    showMore: 'Show More',
    atLeastOneField: 'Please fill in at least one health metric',
    addSuccess: 'Health record added successfully',
    addFailed: 'Failed to add health record, please retry',
  },
  medication: {
    title: 'Medication Management',
    currentPlan: 'Current Plan',
    noMedications: 'No medications',
    addMedication: 'Add Medication',
    name: 'Medication Name',
    dosage: 'Dosage',
    frequency: 'Frequency',
    freqDaily: 'Once Daily',
    freqTwice: 'Twice Daily',
    freqThree: 'Three Times Daily',
    freqWeekly: 'Once Weekly',
    time: 'Time',
    startDate: 'Start Date',
    addBtn: 'Add',
    interactionWarning: 'Drug Interaction Warning',
    noInteraction: 'No drug interaction risks',
    calendar: 'Medication Calendar',
    invalidTimeFormat: 'Invalid time format, please use HH:mm (e.g., 08:00, 20:00)',
    addSuccess: 'Medication added successfully',
    addFailed: 'Failed to add medication, please retry',
    takeSuccess: 'Dose checked in successfully',
    takeFailed: 'Failed to record dose, please retry',
    deleteSuccess: 'Medication deleted',
    deleteFailed: 'Failed to delete medication, please retry',
    deleteConfirmTitle: 'Delete Medication',
    deleteConfirmMessage: 'Are you sure you want to delete this medication? This action cannot be undone.',
  },
  sos: {
    title: 'Emergency Call',
    holdToCall: 'Hold to Call',
    holdHint: 'Hold for 1 second to trigger emergency call',
    sent: 'Emergency Call Sent',
    sentDesc: 'Your emergency contacts have been notified. Please stay calm and wait for help',
    contacts: 'Emergency Contacts',
    location: 'Current Location',
    loadingLocation: 'Getting location...',
    locationUnavailable: 'Location unavailable',
    history: 'SOS History',
    noHistory: 'No SOS records',
    confirmed: 'Confirmed',
    pending: 'Pending',
    sendFailed: 'Failed to send emergency alert, please retry',
    confirmFailed: 'Failed to confirm, please retry',
    resolveFailed: 'Failed to resolve, please retry',
    resolved: 'Resolved',
  },
  family: {
    title: 'Family Care',
    members: 'Linked Family',
    noMembers: 'No linked family members',
    bindCode: 'Binding Code',
    bindCodeHint: 'Share this code with family members to link accounts',
    invite: 'Invite Family',
    email: 'Family Email',
    relationship: 'Relationship',
    relSon: 'Son',
    relDaughter: 'Daughter',
    relSpouse: 'Spouse',
    relSibling: 'Sibling',
    relOther: 'Other',
    inviteBtn: 'Send Invite',
    notifications: 'Notification Preferences',
    prefAbnormal: 'Health Alert',
    prefMedication: 'Medication Reminder',
    prefSos: 'Emergency Alert',
    prefWeekly: 'Weekly Report',
    prefCheckin: 'Check-in Notification',
    dataSharing: 'Data Sharing',
    shareDesc: 'Allow family to view your health data',
    invalidEmail: 'Please enter a valid email address',
    inviteSent: 'Invitation sent successfully',
    inviteFailed: 'Failed to send invitation, please retry',
    elderData: 'Elder Data',
    viewElderData: 'View Elder Data',
    removeConfirmTitle: 'Remove Family Member',
    removeConfirmMessage: 'Are you sure you want to remove {name}?',
    removeSuccess: 'Family member removed',
    removeFailed: 'Failed to remove family member, please retry',
  },
  article: {
    title: 'Health Articles',
    search: 'Search articles...',
    noArticles: 'No articles found',
    expand: 'Read More',
    collapse: 'Collapse',
    categories: {
      all: 'All',
      heart: 'Heart',
      diabetes: 'Diabetes',
      bone: 'Bone',
      sleep: 'Sleep',
      nutrition: 'Nutrition',
      exercise: 'Exercise',
    },
  },
  checkin: {
    title: 'Daily Check-in',
    feeling: 'How Do You Feel',
    feelings: {
      great: 'Great',
      good: 'Good',
      normal: 'Okay',
      bad: 'Bad',
      terrible: 'Terrible',
    },
    medication: 'Medication Today',
    yes: 'Yes',
    no: 'No',
    diet: 'Diet Rating',
    dietRatings: {
      good: 'Good',
      normal: 'Okay',
      poor: 'Poor',
    },
    exercise: 'Exercise Today',
    mood: 'Mood Today',
    submit: 'Submit Check-in',
    streak: 'Streak',
    streakDays: 'days',
    calendar: 'Check-in Calendar',
    success: 'Check-in successful',
    failed: 'Check-in failed, please retry',
  },
  settings: {
    title: 'Settings',
    profile: 'Profile',
    name: 'Username',
    phone: 'Phone',
    birthday: 'Birthday',
    emergencyContact: 'Emergency Contact',
    save: 'Save Changes',
    preferences: 'Preferences',
    language: 'Language',
    theme: 'Theme',
    light: 'Light',
    dark: 'Dark',
    notifications: 'Notifications',
    notifHealth: 'Health Alerts',
    notifMedication: 'Medication Reminders',
    notifSos: 'Emergency Alerts',
    notifCheckin: 'Check-in Reminders',
    bindCode: 'My Binding Code',
    changePassword: 'Change Password',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    changePasswordBtn: 'Change Password',
    profileSaved: 'Profile saved successfully',
    profileSaveFailed: 'Failed to save profile, please retry',
    passwordChanged: 'Password changed successfully',
    passwordChangeFailed: 'Failed to change password, please retry',
    dangerZone: 'Danger Zone',
    deleteDesc: 'Account deletion is irreversible. All data will be lost',
    deleteAccount: 'Delete Account',
    deleteConfirm: 'Are you sure? This action cannot be undone!',
    subscription: 'Subscription',
    subscriptionDesc: 'Upgrade to premium to unlock all health management features',
    monthlyPlan: 'Monthly Plan',
    yearlyPlan: 'Yearly Plan',
    month: 'month',
    year: 'year',
    subscribeNow: 'Subscribe Now',
    bestValue: 'Best Value',
  },
  about: {
    title: 'About ElderCare',
    subtitle: 'Smart Health Guardian for Seniors',
    intro: 'Introduction',
    introContent: 'ElderCare is a health management platform designed for seniors, featuring smart health monitoring, medication management, emergency calls, and more to help seniors manage their health while keeping families connected.',
    version: 'Version Info',
    versionNumber: 'Version',
    buildDate: 'Build Date',
    acknowledgements: 'Acknowledgements',
    contact: 'Contact',
    contactEmail: "q3300404157{'@'}gmail.com",
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
  },
  notfound: {
    message: 'Sorry, the page you visited does not exist',
    goHome: 'Go Home',
  },
  privacy: {
    title: 'Privacy Policy',
    sections: {
      collection: {
        title: 'Information Collection',
        content: 'We only collect personal information necessary for providing services, including registration details (email, name), health data (blood pressure, blood sugar and other metrics), and device information. We do not collect any personal information unrelated to our services.',
      },
      usage: {
        title: 'Information Usage',
        content: 'Your personal information is only used to provide and improve services, including health data analysis, abnormal health alerts, medication reminders and other core features. We will not use your information for any unauthorized purposes.',
      },
      storage: {
        title: 'Information Storage',
        content: 'Your data is stored on secure cloud servers with encrypted transmission and storage. We take reasonable technical measures to protect your data security and prevent unauthorized access.',
      },
      sharing: {
        title: 'Information Sharing',
        content: 'Without your explicit consent, we will not share your personal information with any third party. Health data will only be shared with linked family members after you authorize the family connection.',
      },
      rights: {
        title: 'Your Rights',
        content: 'You have the right to access, correct, and delete your personal information, as well as the right to withdraw your consent. You can exercise these rights through the in-app settings or by contacting our support team.',
      },
      security: {
        title: 'Security',
        content: 'We use industry-standard encryption and security measures to protect your data, including SSL encrypted transmission, encrypted data storage, and access control. We regularly review and update our security measures.',
      },
    },
  },
  terms: {
    title: 'Terms of Service',
    sections: {
      acceptance: {
        title: 'Acceptance',
        content: 'By using this application, you agree to comply with these Terms of Service. If you do not agree with any part of these terms, please stop using this application. We reserve the right to modify these terms at any time.',
      },
      service: {
        title: 'Service Description',
        content: 'ElderCare provides health management assistance services, including health data recording, medication reminders, emergency calls and other features. This service is for reference only and does not constitute medical advice, nor can it replace professional medical services.',
      },
      accounts: {
        title: 'Account Management',
        content: 'You need to register an account to use the core features of this application. You are responsible for keeping your account password secure and for all activities under your account. If you discover any unauthorized use of your account, please notify us immediately.',
      },
      data: {
        title: 'Data & Privacy',
        content: 'We value your privacy protection. For detailed data handling rules, please refer to our Privacy Policy. You retain full control over the health data you submit.',
      },
      liability: {
        title: 'Limitation of Liability',
        content: 'The health information provided by this application is for reference only and does not constitute medical diagnosis or treatment advice. We are not liable for any losses resulting from the use of information from this application. If you have health concerns, please consult a qualified healthcare professional.',
      },
      changes: {
        title: 'Changes',
        content: 'We may update these Terms of Service from time to time. Significant changes will be communicated through in-app notifications or email. Continued use of this application constitutes acceptance of the updated terms.',
      },
    },
  },
  feedback: {
    title: 'Feedback',
    subtitle: 'Your feedback is very important to us',
    name: 'Name',
    namePlaceholder: 'Enter your name (optional)',
    email: 'Email',
    emailPlaceholder: 'Enter your email',
    subject: 'Subject',
    subjectGeneral: 'General Feedback',
    subjectBug: 'Bug Report',
    subjectFeature: 'Feature Request',
    subjectOther: 'Other',
    message: 'Message',
    messagePlaceholder: 'Please describe your feedback in detail...',
    submit: 'Submit Feedback',
    submitting: 'Submitting...',
    success: 'Feedback sent successfully! Thank you for your input',
    error: 'Failed to send feedback, please try again later',
  },
  verify: {
    loading: 'Verifying your email...',
    success: 'Verification Successful!',
    successDesc: 'Your email has been verified. You can now log in',
    failed: 'Verification Failed',
    failedDesc: 'The verification link is invalid or expired. Please resend the verification email',
    resend: 'Resend Verification Email',
    resending: 'Sending...',
    goLogin: 'Go to Login',
  },
  a11y: {
    toggleTheme: 'Toggle theme',
    selectLanguage: 'Select language',
    selectRole: 'Select role',
  },
  theme: {
    light: 'Light',
    dark: 'Dark',
  },
}
